import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, of, switchMap, tap } from 'rxjs';

import { AdminService } from '@admin/admin.service';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import { DocumentsService } from '@documents/services/documents.service';
import { DeleteDialogComponent } from '@shared/dialogs/components';

import {
  CONFIGURATION_DIALOG_ACTIONS,
  CONFIGURATION_DOCUMENT_TYPE_ACTIONS,
  CONFIGURATION_SUBMISSION_TYPE_ACTIONS,
} from '../actions';
import { DocumentTypes, ConfigSubmissions } from '../types';
import { CONFIGURE_DEFAULT_PARAMS } from '../constants';
import { constructDeleteDialogData } from '../utils';

@Injectable()
export class ConfigurationsEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _adminService = inject(AdminService);
  private readonly _wcgTableUtils = inject(WcgTableUtilsService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _documentService = inject(DocumentsService);
  private readonly _store = inject(Store);
  private readonly _destroyRef = inject(DestroyRef);

  /**
   * @DOCUMENTTYPES EFFECTS
   * */
  loadDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadDocumentTypes),
      switchMap(({ params }) => {
        const httpParams = this._wcgTableUtils.generateTableQueryParams(params);

        return this._adminService.getAllDocumentTypeList(httpParams).pipe(
          switchMap((documentTypes: DocumentTypes) => {
            return [
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onLoadDocumentTypesSuccess({
                documentTypes,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onLoadDocumentTypesFailure({
                error,
              }),
            ),
          ),
        );
      }),
    ),
  );

  loadAttributes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadAttributes),
      switchMap(({ params }) => {
        const httpParams = this._wcgTableUtils.generateTableQueryParams(params);
        return this._adminService.getAllAttributes(httpParams).pipe(
          switchMap((documentAttributes) => {
            return [
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onLoadAttributesSuccess({
                documentAttributes,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onLoadAttributeFailure({
                error,
              }),
            ),
          ),
        );
      }),
    ),
  );

  createDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.createDocumentType),
      switchMap(({ data, wcgTableValues }) =>
        this._adminService.createDocumentType(data).pipe(
          switchMap(() => {
            return [
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onCreateDocumentTypeSuccess(),
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadDocumentTypes({
                params: wcgTableValues,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onCreateDocumentTypeFailure({
                error: error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.updateDocumentType),
      switchMap(({ data }) =>
        this._adminService.updateDocumentType(data).pipe(
          switchMap((response) => {
            return [
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onUpdateDocumentTypeSuccess({
                data: response,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onUpdateDocumentTypeFailure({
                error: error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  checkCanDeleteDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.checkCanDeleteDocument),
      switchMap(({ data }) =>
        this._adminService.checkCanDeleteDocument(data.id).pipe(
          switchMap(({ status, message }) => {
            return [
              CONFIGURATION_DIALOG_ACTIONS.openConfigurationDialog({
                component: () => DeleteDialogComponent,
                data: constructDeleteDialogData(status!, message!),
                id: status! ? data.id : undefined,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onDeleteDocumentTypeFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_DOCUMENT_TYPE_ACTIONS.deleteDocumentType),
      switchMap(({ documentId, message }) =>
        this._adminService.deleteDocumentType(documentId, message).pipe(
          switchMap(() => {
            return [
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onDeleteDocumentTypeSuccess({
                documentId,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onDeleteDocumentTypeFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * @DOCUMENTTYPE EFFECTS END
   * */

  /**
   * @CONFIGURATION DIALOG ACTIONS
   * */
  openConfigurationDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(CONFIGURATION_DIALOG_ACTIONS.openConfigurationDialog),
        tap(({ component, data, id }) => {
          const dialogRef = this._matDialog.open(component(), {
            data: data,
          });
          if ('type' in data) {
            this._customDeleteDialogListener(
              dialogRef,
              data.type === 'DELETE',
              id,
            );
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  /**
   * @SUBMISSIONTYPES EFFECTS
   * */
  loadSubmissions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadSubmissionList),
      switchMap(({ params }) => {
        const httpParams = this._wcgTableUtils.generateTableQueryParams(params);

        return this._adminService.getAllSubmissionsList(httpParams).pipe(
          switchMap((submissions: ConfigSubmissions) => {
            return [
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onLoadsubmissionListSuccess(
                {
                  submissions,
                },
              ),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onLoadsubmissionListFailure(
                {
                  error,
                },
              ),
            ),
          ),
        );
      }),
    ),
  );

  fetchSubmissionDocumentType$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_SUBMISSION_TYPE_ACTIONS.fetchSubmissionDocumentType),
      switchMap(({ submission }) =>
        this._adminService.getSubmissionDocumentTypeList(submission).pipe(
          switchMap((submissionDocument) => {
            return [
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onFetchSubmissionDocumentTypeSuccess(
                {
                  submissionDocument: submissionDocument,
                },
              ),
              CONFIGURATION_DIALOG_ACTIONS.loadSubmissionDialogData({
                entityType: submission.entity_type,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onFetchSubmissionDocumentTypeFailure(
                {
                  error,
                },
              ),
            ),
          ),
        ),
      ),
    ),
  );

  loadSubmissionDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadDocumentTypesForSubmission,
      ),
      switchMap(({ submission }) =>
        this._adminService
          .getSubmissionDocumentTypes(submission.entity_type)
          .pipe(
            switchMap((response) => {
              return [
                CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onLoadDocumentTypesForSubmissionSuccess(
                  {
                    documentTypes: response,
                  },
                ),
                CONFIGURATION_DOCUMENT_TYPE_ACTIONS.loadAttributes({
                  params: {
                    ...CONFIGURE_DEFAULT_PARAMS(),
                    pageSize: 100,
                  },
                }),
                CONFIGURATION_SUBMISSION_TYPE_ACTIONS.fetchSubmissionDocumentType(
                  {
                    submission,
                  },
                ),
              ];
            }),
            catchError((error: Error) =>
              of(
                CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onLoadDocumentTypesForSubmissionFailure(
                  {
                    error,
                  },
                ),
              ),
            ),
          ),
      ),
    ),
  );

  updateIrisSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CONFIGURATION_SUBMISSION_TYPE_ACTIONS.updateIrisSettings),
      switchMap(({ irisMapping, id, wcgTableValues, entityType }) =>
        this._adminService.updateIrisSettings(irisMapping, id, entityType).pipe(
          switchMap(() => {
            return [
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadSubmissionList({
                params: wcgTableValues,
              }),
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateIrisSettingsSuccess(),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateIrisSettingsFailure(
                {
                  error,
                },
              ),
            ),
          ),
        ),
      ),
    ),
  );

  updateSDocumentTypes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        CONFIGURATION_SUBMISSION_TYPE_ACTIONS.updateSubmissionDocumentTypes,
      ),
      switchMap((data) =>
        this._adminService.updateSubmissionDocumentTypes(data).pipe(
          switchMap(() => {
            return [
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateSubmissionDocumentTypesSuccess(),
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.loadSubmissionList({
                params: data.wcgTableValues,
              }),
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.fetchDocumentTypes({
                entityType: data.entityType,
                typeId: data.submissionId,
              }),
            ];
          }),
          catchError((error: Error) =>
            of(
              CONFIGURATION_SUBMISSION_TYPE_ACTIONS.onUpdateSubmissionDocumentTypesFailure(
                {
                  error,
                },
              ),
            ),
          ),
        ),
      ),
    ),
  );

  fetchDocumentTypes$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(CONFIGURATION_SUBMISSION_TYPE_ACTIONS.fetchDocumentTypes),
        switchMap(({ typeId, entityType }) =>
          this._documentService.fetchDocumentTypes(typeId, entityType).pipe(
            tap(() => {}),
            catchError((error: Error) => {
              console.error('Failed to fetch document types:', error);
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  private _customDeleteDialogListener(
    dialogRef: MatDialogRef<unknown>,
    canDelete: boolean,
    id: string | undefined,
  ) {
    if (dialogRef) {
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (data: string) => {
            if (data?.length > 0 && id) {
              this._store.dispatch(
                CONFIGURATION_DOCUMENT_TYPE_ACTIONS.deleteDocumentType({
                  documentId: id,
                  message: data,
                }),
              );
            }
          },
        });
    }
  }
}
